import { useCallback, useContext, useEffect, useReducer, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";

import BreadCrumb from "../components/breadCrumb/BreadCrumb";
import DcsdFormModal from "../components/modals/DcsdFormModal.jsx";
import FormReducer from "../utils/FormReducer";
import GradeBlock from "../components/gradeLevelBlock/GradeBlock.jsx";
import GuardianAddressBlock from "../components/addressBlock/GuardianAddressBlock.jsx";
import Header from "../components/header/Header.jsx";
import InvalidDobModalForm from "../components/formComponents/modalForms/InvalidDobModalForm.jsx";
import LoadingSvg from "../components/loadingSvg/LoadingSvg.jsx";
import NoEscape from "../utils/NoEscape.jsx";
import OpenEnrollmentDao from "../dao/OpenEnrollmentDao";
import RbA from "../components/rba/RbA";
import SaveBlock from "../components/saveBlock/SaveBlock";
import StudentForm from "../components/formComponents/forms/StudentForm";
import StudentInfoDao from "../dao/StudentInfoDao";
import T from "../components/translations/Translations.jsx";
import Toolbar from "../components/Toolbar.jsx";
import UserDao from "../dao/UserDao";
import ValidationPatterns from "../utils/ValidationPatterns";
import WindowAndPermissionDialog from "../components/modals/WindowAndPermissionDialog.jsx";

import { cleanAddress } from "../utils/StringUtils.jsx";
import { getTodayWithTime, getEpoch } from "../utils/DateFormatter.jsx";
import { GlobalContext } from "../components/contextProvider/ContextProvider";
import { gradeTranslator } from "../const/Translations.jsx";
import { isValidDate } from "../utils/DateFormatter.jsx";

import "react-toastify/dist/ReactToastify.css";

/**
 * This will render the form and components for showing and submitting a new Open Enrollment record
 * Existing & External Guardian + New Student (IC data save to OE data)
 * @name StudentNew
 * @returns {JSX.Element}
 */
const StudentNew = () => {
    const { state } = useContext(GlobalContext);
    const { adwCalled, oeActiveDistrictWindow, oeWindows, token, userDetails } = state;

    const navigate = useNavigate();

    const initialFormState = {
        addressLine1: "",
        addressLine2: "",
        birthdate: "",
        birthMonth: "",
        birthDay: "",
        birthYear: "",
        city: "",
        firstName: "",
        gender: "",
        gradeName: "",
        gradeApplying: "",
        key: null,
        lastName: "",
        middleName: "",
        searchAddress: "",
        schoolYearKey: oeActiveDistrictWindow ? oeActiveDistrictWindow.schoolYearKey : "",
        state: "",
        studentNumber: null,
        zip: ""
    };

    const allowedRolesArray = ["EXTERNAL_GUARDIAN", "GUARDIAN"];

    const [birthdateFlag, setBirthdateFlag] = useState(false);
    const [enableButton, setEnableButton] = useState(false);
    const [formState, formDispatch] = useReducer(FormReducer, initialFormState);
    const [guardianInfo, setGuardianInfo] = useState(null);
    const [hasArcAddress, setHasArcAddress] = useState(false); // this is (true/false) to disable fields in StudentForm.
    const [loader, setLoader] = useState(false);
    const [newOeStudent, setNewOeStudent] = useState(false);
    const [open, setOpen] = useState("false");
    const [outOfBounds, setOutOfBounds] = useState(false);
    const [schoolYearRange, setSchoolYearRange] = useState(null);
    const [showDobResetButton, setShowDobResetButton] = useState(false);
    const [sorryMessage, setSorryMessage] = useState("");
    const [sorryTitle, setSorryTitle] = useState("");
    const [studentInfoGate, setStudentInfoGate] = useState(false);
    const [validatedBirthdayDto, setValidatedBirthdayDto] = useState(null);

    const oeButtonsBackToDashboard = T({ key: "OE_Buttons_Back_To_Dashboard" });

    const toastDataError = T({ key: "OE_Toast_Data_Error" });
    const toastStudentAdded = T({ key: "OE_Toast_Student_Added" });
    const toastValidBirthday = T({ key: "OE_Toast_Valid_Birthday" });

    const dialogClosedTitle = T({ key: "OE_Dialog_Window_Closed_Title" });
    const dialogClosedBody = T({ key: "OE_Dialog_Window_Closed_Body" });
    const dialogIneligibleTitle = T({ key: "OE_Dialog_Ineligible_Title" });
    const buttonsAcceptProceed = T({ key: "OE_Buttons_Accept_Proceed" });
    const buttonsShouldNotMatch = T({ key: "OE_Buttons_Should_Not_Match" });
    const modalNamesMatchTitle = T({ key: "OE_Modal_Names_Match_Title" });
    const modalNamesMatchBody1 = T({ key: "OE_Modal_Names_Match_Body_1" });
    const modalNamesMatchBody2 = T({ key: "OE_Modal_Names_Match_Body_2" });
    const modalNamesMatchBody3 = T({ key: "OE_Modal_Names_Match_Body_3" });
    const modalNamesMatchNote = T({ key: "OE_Modal_Names_Match_Note" });
    const modalNamesCorrectionTitle = T({ key: "OE_Modal_Names_Correction_Title" });
    const modalNamesCorrectionBody = T({ key: "OE_Modal_Names_Correction_Body" });

    /**
     * NoEscape prevents the escape key from closing modals
     */
    NoEscape();

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (["birthMonth", "birthDay", "birthYear"].includes(name)) {
            let newVal = value;
            setBirthdateFlag(false);
            if (!ValidationPatterns.digitOnly.test(value) && value !== "") {
                return false;
            }
            if (["birthDay", "birthMonth"].includes(name)) {
                newVal = value !== "" ? parseInt(value, 10) : "";
                if (name === "birthDay") {
                    if (newVal !== "" && newVal > 31) {
                        return false;
                    }
                } else if (name === "birthMonth") {
                    if (newVal !== "" && newVal > 12) {
                        return false;
                    }
                }
                if (newVal.toString().length > 0 && newVal > 0) {
                    newVal = newVal.toString().padStart(2, "0");
                }
                formDispatch({
                    type: "text",
                    field: name,
                    payload: newVal
                });

                return true;
            }
        }
        if (name === "zip") {
            if (!ValidationPatterns.digitOnly.test(value) && value !== "") {
                return false;
            }
        }
        if (["addressLine1", "addressLine2", "city"].includes(name)) {
            formDispatch({
                type: "text",
                field: name,
                payload: value.toUpperCase()
            });
        } else {
            formDispatch({
                type: "text",
                field: name,
                payload: value
            });
        }
        return true;
    };

    const getStudentData = useCallback(() => {
        return {
            address: {
                addressLine1: cleanAddress(formState.addressLine1?.toUpperCase()),
                addressLine2: formState.addressLine2 ? formState.addressLine2?.toUpperCase() : null,
                city: cleanAddress(formState.city?.toUpperCase()),
                state: formState.state,
                zip: formState.zip.trim()
            },
            birthdate: formState.birthdate,
            birthMonth: formState.birthMonth,
            birthDay: formState.birthDay,
            birthYear: formState.birthYear,
            feederLocationKey: null,
            firstName: formState?.firstName,
            gender: formState.gender,
            gradeApplying: formState.gradeApplying,
            gradeName: formState.gradeName,
            gridCode: null,
            key: null,
            lastName: formState?.lastName,
            locationKey: null,
            middleName: formState?.middleName,
            oodSchoolName: null,
            oodSchoolCity: null,
            oodSchoolState: null,
            overrideStatus: "DEFAULT_LOCATION_CHECK",
            parentGuid: userDetails.uid,
            personId: null,
            schoolYearKey: oeActiveDistrictWindow?.schoolYearKey,
            studentNumber: null
        };
    }, [formState, oeActiveDistrictWindow, userDetails]);

    /**
     * @name handleSubmit
     * @returns
     */
    const handleSubmit = () => {
        const newStudentData = getStudentData();

        if (token && !newOeStudent) {
            const options = {
                action: "oeNewStudentPost",
                data: newStudentData,
                token
            };
            setLoader(true);
            OpenEnrollmentDao(options)
                .then((response) => {
                    if (response) {
                        const { errors, payload } = response.data;
                        if (payload && payload !== null) {
                            toast.success(toastStudentAdded, { autoClose: 3000 });
                            // change session storage so that we can redirect to school-choice
                            sessionStorage.setItem("orig-path", `/school-choice/${payload.key}`);
                            sessionStorage.setItem("student-key", JSON.stringify(parseInt(payload.key, 10)));
                            // Set cookie for the entire domain with a short expiry (e.g., 1 hour)
                            document.cookie = `newStudentKey=${payload.key}; Max-Age=3600; path=/; domain=.dcsdk12.org; Secure; SameSite=Lax`;
                            setTimeout(() => {
                                navigate(`/school-choice/${payload.key}`);
                            }, 3000);
                        }
                        if (errors) {
                            setEnableButton(false);
                            toast.error(errors[0], { autoClose: false });
                        }
                        setNewOeStudent(true); // -> disable text fields
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    };

    const handlePreSubmit = () => {
        if (
            formState.firstName?.trim().toLowerCase() === userDetails?.givenName?.trim().toLowerCase() &&
            formState.lastName?.trim().toLowerCase() === userDetails?.surname?.trim().toLowerCase()
        ) {
            setOpen("duplicateParentName");
        } else {
            handleSubmit();
        }
    };

    /**
     * Reset the birthdate on button click
     */
    const birthdateReset = () => {
        setValidatedBirthdayDto(null); // reset the DTO to null
        setBirthdateFlag(false); // flag for waiting on api call to validate dob
        setShowDobResetButton(false); // shows reset button

        const tempFormState = formState;
        tempFormState.birthdate = "";
        tempFormState.birthDay = "";
        tempFormState.birthMonth = "";
        tempFormState.birthYear = "";
        tempFormState.gradeApplying = "";
        tempFormState.gradeName = "";

        formDispatch({
            type: "reset",
            payload: { ...tempFormState }
        });
    };

    /**
     * When formState.birthdate value is true make a call to BE service
     * that will validate and provide current & applying grades
     * @name getValidDOB
     * @param {string} dob
     * @callback
     */
    const getValidDOB = useCallback(
        (dob) => {
            const options = {
                action: "oeStudentBirthdate",
                params: {
                    birthDate: dob
                },
                token
            };
            setLoader(true);
            OpenEnrollmentDao(options)
                .then((response) => {
                    if (response) {
                        const { errors, payload } = response.data;
                        if (payload) {
                            setValidatedBirthdayDto(payload);
                        } else {
                            setValidatedBirthdayDto({ error: errors[0] });
                            setEnableButton(false);
                        }
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setLoader(false);
                });
        },
        [token]
    );

    const showGradeBlock = () => {
        return formState && formState.gradeName?.length > 0 && formState.gradeApplying?.length > 0 && schoolYearRange;
    };

    /**
     * Get the schoolYearKey and dispatch to state (contextProvider)
     * this call is from OE current district window
     */
    useEffect(() => {
        if (adwCalled && !oeActiveDistrictWindow) {
            setOutOfBounds(true);
            setSorryTitle(dialogClosedTitle);
            setSorryMessage(dialogClosedBody);
            setOpen("out-of-bounds");
        }
    }, [adwCalled, dialogClosedBody, dialogClosedTitle, oeActiveDistrictWindow]);

    /**
     * Get IC guardian information (infinite campus)
     */
    useEffect(() => {
        if (token && userDetails && !guardianInfo) {
            const { roleDtos } = userDetails;
            const match = roleDtos.filter((obj) => obj.role === "GUARDIAN");
            if (match && match.length) {
                const options = {
                    action: "guardianInfoRead",
                    username: userDetails?.username,
                    token
                };
                setLoader(true);
                StudentInfoDao(options)
                    .then((response) => {
                        if (response) {
                            const { errors, payload } = response.data;

                            if (payload) {
                                setGuardianInfo(payload);

                                // If there is user info then dispatch it to formState
                                // and show the below fields on page load.
                                const { addressDto } = payload;
                                const { addressLine1, city, state, zip } = addressDto;
                                const tempFormState = formState;
                                tempFormState.addressLine1 = addressLine1;
                                tempFormState.city = city;
                                tempFormState.state = state;
                                tempFormState.zip = zip;
                            }
                            if (errors && errors.length) {
                                setGuardianInfo(null);
                                toast.error(toastDataError);
                            }
                        }
                    })
                    .catch((error) => {
                        console.error(error);
                    })
                    .finally(() => {
                        setLoader(false);
                    });
            } else {
                setGuardianInfo(userDetails);
            }
        }
    }, [formState, guardianInfo, toastDataError, token, userDetails]);

    useEffect(() => {
        if (adwCalled && oeWindows) {
            if (!oeWindows.isParentRequestAllowed && !oeWindows.isParentRequestRound2Allowed) {
                setOutOfBounds(true);
                setSorryTitle(dialogClosedTitle);
                setSorryMessage(dialogClosedBody);
                setOpen("out-of-bounds");
            }
        }
    }, [adwCalled, dialogClosedBody, dialogClosedTitle, oeWindows]);

    /**
     * Get the school year range from DB
     */
    useEffect(() => {
        if (token && oeActiveDistrictWindow && !schoolYearRange) {
            const nextSchoolYear = parseInt(oeActiveDistrictWindow.schoolYearKey, 10) + 1;
            const options = {
                action: "userSchoolYearRange",
                schoolYearKey: `${nextSchoolYear}`, // needs to be sent as a string
                token
            };
            setLoader(true);
            UserDao(options)
                .then((response) => {
                    if (response) {
                        const { payload } = response.data;
                        if (payload) {
                            setSchoolYearRange(payload);
                        }
                    }
                })
                .catch((error) => {
                    console.error(error);
                })
                .finally(() => {
                    setLoader(false);
                });
        }
    }, [oeActiveDistrictWindow, schoolYearRange, token]);

    /**
     * Check if the Open Enrollment window is open or closed
     * If outOfBounds === false, then the window is open
     *  */
    useEffect(() => {
        if (oeActiveDistrictWindow && !outOfBounds) {
            const { endDate, startDate } = oeActiveDistrictWindow;
            const today = getTodayWithTime();
            if (getEpoch(endDate) > getEpoch(today) && getEpoch(startDate) < getEpoch(today)) {
                setOutOfBounds(false);
            } else {
                setOutOfBounds(true);
                setSorryTitle(dialogClosedTitle);
                setSorryMessage(dialogClosedBody);
                setOpen("out-of-bounds");
            }
        }
    }, [dialogClosedBody, dialogClosedTitle, oeActiveDistrictWindow, outOfBounds]);

    /**
     * This is only for showing the "Clear Birthdate Fields" button
     */
    useEffect(() => {
        if (formState.birthYear || formState.birthMonth || formState.birthDay) {
            setShowDobResetButton(true);
        } else {
            setShowDobResetButton(false);
        }
    }, [formState, showDobResetButton]);

    /**
     * Call getValid DOB if UI month, day, year is filled out.
     */
    useEffect(() => {
        const { birthDay, birthMonth, birthYear } = formState;
        if (parseInt(birthDay, 10) !== 0 || parseInt(birthMonth, 10) !== 0) {
            const studentBirthDate = `${birthYear}-${birthMonth}-${birthDay}`;
            if (studentBirthDate && studentBirthDate.length === 10 && !birthdateFlag) {
                if (isValidDate(studentBirthDate)) {
                    setBirthdateFlag(true);
                    getValidDOB(studentBirthDate);
                } else if (!isValidDate(studentBirthDate)) {
                    toast.error(toastValidBirthday, { autoClose: 5000 });
                }
            }
        }
    }, [birthdateFlag, formDispatch, formState, getValidDOB, toastValidBirthday]);

    /**
     * If a valid birthdate is entered in UI then get the values from validateBirthdteDto
     * and dispatch  (inject) them into formState
     */
    useEffect(() => {
        if (validatedBirthdayDto && validatedBirthdayDto?.currentGrade) {
            const updatedFormState = formState;
            updatedFormState.gradeName = validatedBirthdayDto?.currentGrade;
            updatedFormState.gradeApplying = validatedBirthdayDto?.gradeApplying;
            updatedFormState.birthdate = `${formState.birthYear}-${formState.birthMonth}-${formState.birthDay}`;

            formDispatch({
                type: "reset",
                payload: { ...updatedFormState }
            });
        } else if (validatedBirthdayDto?.error) {
            setOpen("dobRange");
        }
        // eslint-disable-next-line
    }, [formDispatch, validatedBirthdayDto]);

    /**
     * This useEffect is checking the formState for validation.
     * if any of the fields below are not filled out, the save button will not be active
     */
    useEffect(() => {
        if (
            formState.addressLine1?.trim().length &&
            formState.birthdate &&
            formState.birthMonth &&
            formState.birthDay &&
            formState.birthYear &&
            formState.city?.trim().length > 0 &&
            formState.firstName?.trim().length > 0 &&
            formState.gender?.trim().length &&
            formState.lastName?.trim().length > 0 &&
            formState.state?.trim().length > 0 &&
            formState.zip?.trim().length > 0 &&
            gradeTranslator(formState.gradeName) !== "-" &&
            gradeTranslator(formState.gradeApplying) !== "-"
        ) {
            setEnableButton(true);
        } else {
            setEnableButton(false);
        }
    }, [formState, enableButton]);

    return (
        <RbA allowedRoles={allowedRolesArray} redirect="/notFound">
            <Header />
            <Toolbar />
            <BreadCrumb label={`Open Enrollment > ${T({ key: "Breadcrumb_New_Student_Create" })}`} />
            {!outOfBounds && (
                <>
                    <div className="gutter-90">
                        <GuardianAddressBlock guardianInfo={guardianInfo} />
                        <div className="mt-5 mb-3">
                            <hr />
                        </div>
                        <StudentForm
                            birthdateFlag={birthdateFlag}
                            birthdateReset={birthdateReset}
                            formState={formState}
                            formDispatch={formDispatch}
                            handleChange={handleChange}
                            hasArcAddress={hasArcAddress}
                            setHasArcAddress={setHasArcAddress}
                            showDobResetButton={showDobResetButton}
                        />
                        {showGradeBlock() && (
                            <GradeBlock
                                formDispatch={formDispatch}
                                formState={formState}
                                schoolYearRange={schoolYearRange}
                            />
                        )}
                    </div>
                    {oeActiveDistrictWindow && (
                        <SaveBlock
                            enableButton={enableButton}
                            formState={formState}
                            handleSubmit={handlePreSubmit}
                            loader={loader}
                            open={open}
                            setOpen={setOpen}
                            setStudentInfoGate={setStudentInfoGate}
                            studentInfoGate={studentInfoGate}
                            validatedBirthdayDto={validatedBirthdayDto}
                        />
                    )}
                    <ToastContainer style={{ width: "50%" }} />
                    {validatedBirthdayDto?.error && (
                        <DcsdFormModal
                            ariaLabel="Birthdate Dialog"
                            birthdateReset={birthdateReset}
                            buttonCount="1"
                            button1Label="Close"
                            button1Labe2="button2"
                            id="dobRange"
                            open={open}
                            setOpen={setOpen}
                            title={dialogIneligibleTitle}
                        >
                            <InvalidDobModalForm />
                        </DcsdFormModal>
                    )}
                    {/* Show this if student (first / last) name matches guardian (first / last) names */}
                    <DcsdFormModal
                        ariaLabel="Pop up modal if user name is same as student name"
                        button1Label={buttonsShouldNotMatch}
                        button2Label={buttonsAcceptProceed}
                        handleSubmit={handleSubmit}
                        id="duplicateParentName"
                        loader={loader}
                        open={open}
                        setOpen={setOpen}
                        title={modalNamesMatchTitle}
                    >
                        <form>
                            <div>
                                <strong>{modalNamesMatchBody1}</strong>
                            </div>
                            <br />
                            <div
                                className="d-flex justify-content-between"
                                style={{ maxWidth: "500px", borderBottom: "1px solid #0669B3" }}
                            >
                                <div>{modalNamesMatchBody2}</div>
                                <div>
                                    <strong>
                                        {formState?.firstName} {formState?.lastName}
                                    </strong>
                                </div>
                            </div>
                            <div
                                className="mt-3 d-flex justify-content-between"
                                style={{ maxWidth: "500px", borderBottom: "1px solid #0669B3" }}
                            >
                                <div>{modalNamesMatchBody3}</div>
                                <div>
                                    {/* Below is for Existing Parent */}
                                    <strong>
                                        {guardianInfo?.givenName} {guardianInfo?.surname}
                                    </strong>
                                    {/* Below is for External (new) Parent */}
                                    <strong>
                                        {guardianInfo?.firstName} {guardianInfo?.lastName}
                                    </strong>
                                </div>
                            </div>
                            <span
                                className="d-flex flex-row-reverse"
                                style={{ maxWidth: "500px", fontSize: ".8rem", fontStyle: "oblique" }}
                            >
                                {modalNamesMatchNote}
                            </span>
                        </form>
                    </DcsdFormModal>
                    {/* If student (first / last) name matches guardian (first / last) names and its not supposed to
                        show this modal. Do not allow to close screen. Only option is back to dashboard button
                    */}
                    <DcsdFormModal
                        ariaLabel="popup modal, if username and student name are same then seek help by calling number below"
                        button1Label={oeButtonsBackToDashboard}
                        handleSubmit={handleSubmit}
                        id="incorrect-name"
                        loader={loader}
                        open={open}
                        setOpen={setOpen}
                        title={modalNamesCorrectionTitle}
                    >
                        <form>{modalNamesCorrectionBody}</form>
                    </DcsdFormModal>
                </>
            )}
            {loader && <LoadingSvg />}
            <WindowAndPermissionDialog
                id="out-of-bounds"
                open={open}
                setOpen={setOpen}
                sorryTitle={sorryTitle}
                sorryMessage={sorryMessage}
            />
        </RbA>
    );
};

export default StudentNew;
